import { AnimatedTime } from "@msuite/ui/animated";
import { Button } from "@msuite/ui/button";
import { useLoading } from "@msuite/ui/hooks";
import { getAttendanceWorksiteIconName, Icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { useAsyncModal } from "@msuite/ui/modal";
import { Tooltip } from "@msuite/ui/tooltip";
import { cn, responseHandler } from "@msuite/ui/utils";
import {
	getAttendanceWorksiteColor,
	getAttendanceWorksiteTranslationKey,
	runCheckInSteps,
} from "@msuite/utils/attendances";
import type { PostAttendanceBodySchema } from "@msuite/utils/schemas";
import type { CheckInRequirements, Enums } from "@msuite/utils/types";
import { motion } from "motion/react";
import { useId, useRef, useState } from "react";
import { getCheckInRequirements } from "@/api/attendances";
import { client } from "@/hono";
import { useAttendances } from "@/hooks/use-attendances";
import { AttendanceAcknowledgementsModal } from "@/modals/users/attendance-acknowledgements-modal";
import { AttendanceRiskAssessmentsModal } from "@/modals/users/attendance-risk-assessments-modal";

const WORKSITE_OPTIONS = [
	"office",
	"home-office",
] as const satisfies Enums<"attendanceWorksiteType">[];

export function CurrentAttendance() {
	/** Hooks */
	const { liveState, calculatedLiveTime, refetch } = useAttendances();
	const loader = useLoading();
	const { t } = useTranslation();
	const id = useId();
	const acknowledgementsController = useAsyncModal<string[]>();
	const riskAssessmentsController = useAsyncModal<boolean>();

	/** State */
	const [selectedWorksite, setSelectedWorksite] =
		useState<Enums<"attendanceWorksiteType">>("office");

	/** Refs */
	// The modals render from this ref rather than from state: the steps run inside the submit, and
	// the requirements are read once per check-in, so there is nothing here to re-render on.
	const requirementsRef = useRef<CheckInRequirements | null>(null);

	/** Functions */
	async function handleOnInsertAttendance() {
		async function handleOnConfirm(geolocation?: GeolocationPosition) {
			try {
				loader.start();

				const json: PostAttendanceBodySchema = {
					coordinates: geolocation
						? {
								latitude: geolocation.coords.latitude,
								longitude: geolocation.coords.longitude,
							}
						: undefined,
				};

				// A check-out ends the live attendance and asks for nothing, so the worksite and the
				// requirements travel on the way in only.
				if (!liveState.isLive) {
					json.worksite = selectedWorksite;
					requirementsRef.current = await getCheckInRequirements({
						worksite: selectedWorksite,
					});
					loader.stop();
					const result = await runCheckInSteps(requirementsRef.current, {
						confirmAcknowledgements: () => acknowledgementsController.confirm(),
						confirmRiskAssessments: async () =>
							(await riskAssessmentsController.confirm()) === true,
					});
					if (!result) return;
					loader.start();
					json.acknowledgedDefinitionIds = result.acknowledgedDefinitionIds;
					json.acknowledgedRiskAssessments = result.acknowledgedRiskAssessments;
				}

				const response = await client.users.attendances.$post({ json });
				await responseHandler(response);
				await refetch();
			} finally {
				loader.stop();
			}
		}
		navigator.geolocation.getCurrentPosition(handleOnConfirm, () => {
			handleOnConfirm();
		});
	}

	/** Render */
	return (
		<>
			<div className="v-stack">
				<div className="h-stack items-center">
					<Button
						isLoading={loader.isLoading}
						variant="ghost"
						className="bg-white/10! rounded-full hover:bg-white/20!"
						onClick={handleOnInsertAttendance}
						icon={
							<Icon
								name={liveState.isLive ? "Square" : "Play"}
								className={cn(
									liveState.isLive ? "stroke-red-500 fill-red-500/20" : "fill-primary-500/10",
								)}
							/>
						}
					/>
					<AnimatedTime
						hours={calculatedLiveTime?.hours}
						minutes={calculatedLiveTime?.minutes}
						seconds={calculatedLiveTime?.seconds}
					/>
					<div className="flex-1" />
					{liveState.liveAttendance ? (
						<motion.div layoutId={`${id}-${liveState.liveAttendance.worksite}`}>
							<Tooltip
								label={t(getAttendanceWorksiteTranslationKey(liveState.liveAttendance?.worksite))}
							>
								<Icon
									name={getAttendanceWorksiteIconName(liveState.liveAttendance?.worksite)}
									size={16}
									stroke={`var(--color-${getAttendanceWorksiteColor(liveState.liveAttendance?.worksite)}-500)`}
								/>
							</Tooltip>
						</motion.div>
					) : (
						<div className="h-stack gap-0.5">
							{WORKSITE_OPTIONS.map((type) => (
								<Tooltip
									key={type}
									label={t(getAttendanceWorksiteTranslationKey(type))}
								>
									<motion.button
										layoutId={`${id}-${type}`}
										type="button"
										onClick={() => setSelectedWorksite(type)}
										className="rounded p-1 transition-opacity"
										style={{ opacity: selectedWorksite === type ? 1 : 0.3 }}
									>
										<Icon
											name={getAttendanceWorksiteIconName(type)}
											size={16}
											stroke={`var(--color-${getAttendanceWorksiteColor(type)}-500)`}
										/>
									</motion.button>
								</Tooltip>
							))}
						</div>
					)}
				</div>
			</div>
			{acknowledgementsController.isOpen && (
				<AttendanceAcknowledgementsModal
					controller={acknowledgementsController}
					definitions={requirementsRef.current?.acknowledgementDefinitions ?? []}
				/>
			)}
			{riskAssessmentsController.isOpen && (
				<AttendanceRiskAssessmentsModal
					controller={riskAssessmentsController}
					riskAssessments={requirementsRef.current?.riskAssessments ?? []}
				/>
			)}
		</>
	);
}
